import { doc, getDoc } from 'firebase/firestore'
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import type { Class } from '@study-decks/shared'

import { getDb } from '../../lib/firebase.js'
import styles from './ClassDetailPage.module.css'

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}

function classFromData(id: string, data: Record<string, unknown>): Class {
  return {
    id,
    googleCourseId: typeof data.googleCourseId === 'string' ? data.googleCourseId : '',
    name: typeof data.name === 'string' ? data.name : '',
    section: typeof data.section === 'string' ? data.section : null,
    teacherIds: stringList(data.teacherIds),
    studentIds: stringList(data.studentIds),
    pendingInviteEmails: stringList(data.pendingInviteEmails),
    domain: typeof data.domain === 'string' ? data.domain : '',
    lastRosterSyncAt: typeof data.lastRosterSyncAt === 'string' ? data.lastRosterSyncAt : '',
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : '',
    status: data.status === 'archived' ? 'archived' : 'active',
  }
}

export default function ClassDetailPage(): JSX.Element {
  const { classId } = useParams<{ classId: string }>()
  const [classData, setClassData] = useState<Class | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  const loadClass = useCallback(async () => {
    if (!classId) return
    setLoading(true)
    setError(null)
    try {
      const snapshot = await getDoc(doc(getDb(), 'classes', classId))
      if (!snapshot.exists()) {
        setError('Class not found.')
        return
      }
      setClassData(classFromData(snapshot.id, snapshot.data()))
    } catch {
      setError('Could not load class.')
    } finally {
      setLoading(false)
    }
  }, [classId])

  useEffect(() => {
    void loadClass()
  }, [loadClass])

  if (loading) {
    return (
      <section className={styles.page} aria-label="Class detail">
        <p role="status" aria-live="polite">
          Loading class…
        </p>
      </section>
    )
  }

  if (error || !classData) {
    return (
      <section className={styles.page} aria-label="Class detail">
        <p role="alert">{error ?? 'Class not found.'}</p>
        <Link to="/teacher/classes">← All classes</Link>
      </section>
    )
  }

  const previewCount = 5
  const visibleStudents = showAll
    ? classData.studentIds
    : classData.studentIds.slice(0, previewCount)

  return (
    <section className={styles.page} aria-labelledby="class-detail-title">
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Class</p>
          <h2 id="class-detail-title">{classData.name}</h2>
          {classData.section ? <p className={styles.muted}>{classData.section}</p> : null}
        </div>
        <nav aria-label="Class actions" className={styles.navLinks}>
          <Link to={`/teacher/classes/${classId}/analytics`} className={styles.navLink}>
            Analytics
          </Link>
          <Link to="/teacher/classes" className={styles.navLink}>
            ← All classes
          </Link>
        </nav>
      </div>

      <div className={styles.rosterPanel}>
        <h3>Roster</h3>
        <p className={styles.rosterCount}>
          {classData.studentIds.length} enrolled student
          {classData.studentIds.length !== 1 ? 's' : ''}
          {classData.pendingInviteEmails.length > 0
            ? `, ${String(classData.pendingInviteEmails.length)} pending invite${classData.pendingInviteEmails.length !== 1 ? 's' : ''}`
            : ''}
        </p>
        {classData.studentIds.length > 0 ? (
          <>
            <p className={styles.rosterNames}>{visibleStudents.join(', ')}</p>
            {classData.studentIds.length > previewCount ? (
              <button
                type="button"
                className={styles.disclosure}
                onClick={() => {
                  setShowAll((prev) => !prev)
                }}
                aria-expanded={showAll}
              >
                {showAll ? 'Show fewer' : `See all ${String(classData.studentIds.length)} students`}
              </button>
            ) : null}
          </>
        ) : (
          <p className={styles.muted}>No enrolled students yet.</p>
        )}
      </div>
    </section>
  )
}
